import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useHistory } from "react-router-dom";

import { DocPrefix, NO_DISPLAY_DATA } from "../../constants";
import { DocSearchQueryContext } from "../../context/doc-search-context";
import { OrgSearchQueryContext } from "../../context/org-search-context";
import { SearchTabContext } from "../../context/search-context";
import { UserSearchQueryContext } from "../../context/user-search-context";
import { documentTypes } from "../search/constants";

const TAB_INDICES = { user: 0, org: 2, doc: 3 } as const;
type TargetTab = keyof typeof TAB_INDICES;

const UUID_RE =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i;

type SearchMode = "id" | "email" | "name" | "doc";

type SearchTypeOption = {
  key: string;
  label: string;
  tab: TargetTab;
  searchMode: SearchMode;
  docType?: string;
};

const DOC_TYPES: SearchTypeOption[] = documentTypes
  .filter((t) => t !== NO_DISPLAY_DATA)
  .sort()
  .map((t) => ({
    key: `doc:${t}`,
    label: t,
    tab: "doc" as const,
    searchMode: "doc" as const,
    docType: t,
  }));

const SEARCH_TYPES: SearchTypeOption[] = [
  { key: "user", label: "User", tab: "user", searchMode: "id" },
  { key: "org", label: "Org", tab: "org", searchMode: "id" },
  { key: "user:email", label: "User Email", tab: "user", searchMode: "email" },
  { key: "user:name", label: "User Name", tab: "user", searchMode: "name" },
  { key: "org:name", label: "Org Name", tab: "org", searchMode: "name" },
  ...DOC_TYPES,
];

const ID_TYPES = SEARCH_TYPES.filter((t) => t.searchMode === "id");
const EMAIL_TYPES = SEARCH_TYPES.filter((t) => t.searchMode === "email");
const NAME_TYPES = SEARCH_TYPES.filter((t) => t.searchMode === "name");

function autoDetectTypeKey(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Email: contains @
  if (trimmed.includes("@")) return "user:email";

  // Known ID prefixes
  if (trimmed.toLowerCase().startsWith(`${DocPrefix.PrefixUser}_`))
    return "user";
  if (trimmed.toLowerCase().startsWith(`${DocPrefix.PrefixRole}_`))
    return "org";
  const prefix = trimmed.split("_")[0].toLowerCase();
  const docMatch = DOC_TYPES.find((t) => t.docType === prefix);
  if (docMatch) return docMatch.key;

  // UUID → user ID search (primary); org is secondary
  if (UUID_RE.test(trimmed)) return "user";

  // Plain text → default to user name search
  return "user:name";
}

const SECONDARY_KEYS: Record<string, string[]> = {
  user: ["doc:user"],
  org: ["doc:organization", "doc:group"],
  "user:name": ["org:name"],
};

function secondaryDocTypeKeys(input: string): string[] {
  const trimmed = input.trim();
  const primaryKey = autoDetectTypeKey(trimmed);
  if (!primaryKey || primaryKey.startsWith("doc:")) return [];
  // UUID: secondary is org ID (not doc:user like the user_ prefix)
  if (UUID_RE.test(trimmed)) return ["org"];
  return SECONDARY_KEYS[primaryKey] ?? [];
}

const GlobalSearchDialog: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const history = useHistory();
  const { setSearchTab, setDocSearchKey, setUserSearchKey, setOrgSearchKey } =
    useContext(SearchTabContext);
  const { setUserSearchQueryState } = useContext(UserSearchQueryContext);
  const { setOrgSearchQueryState } = useContext(OrgSearchQueryContext);
  const { setDocSearchQueryState } = useContext(DocSearchQueryContext);

  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setValue("");
    setSelectedKey(null);
  }, []);

  const trimmed = value.trim();

  const effectiveKey = selectedKey ?? autoDetectTypeKey(trimmed);
  const secondaryKeys = useMemo(
    () => (selectedKey ? [] : secondaryDocTypeKeys(trimmed)),
    [selectedKey, trimmed]
  );

  // Refs so navigate/handleSearch always read current values at call time,
  // avoiding stale closures caused by accumulated history.listen callbacks
  // in search components that update shared context and trigger re-renders.
  const valueRef = useRef(value);
  valueRef.current = value;
  const effectiveKeyRef = useRef(effectiveKey);
  effectiveKeyRef.current = effectiveKey;
  const secondaryKeysRef = useRef(secondaryKeys);
  secondaryKeysRef.current = secondaryKeys;

  const navigate = useCallback(
    (typeKey: string | null) => {
      const currentTrimmed = valueRef.current.trim();
      if (!currentTrimmed) return;

      const type = SEARCH_TYPES.find((t) => t.key === typeKey) ?? null;
      const tab = type?.tab ?? "doc";
      setSearchTab(TAB_INDICES[tab]);

      if (typeKey === "user:email") {
        const atIdx = currentTrimmed.indexOf("@");
        setUserSearchQueryState((prev) => ({
          ...prev,
          userId: "",
          firstName: "",
          lastName: "",
          emailLocal:
            atIdx >= 0 ? currentTrimmed.slice(0, atIdx) : currentTrimmed,
          emailDomain: atIdx >= 0 ? currentTrimmed.slice(atIdx + 1) : "",
          matchExactEmail: false,
        }));
        setUserSearchKey((k) => k + 1);
        history.replace("/search/user");
      } else if (typeKey === "user:name") {
        const parts = currentTrimmed.split(/\s+/);
        setUserSearchQueryState((prev) => ({
          ...prev,
          userId: "",
          emailLocal: "",
          emailDomain: "",
          matchExactEmail: false,
          firstName: parts[0] ?? "",
          lastName: parts.slice(1).join(" "),
        }));
        setUserSearchKey((k) => k + 1);
        history.replace("/search/user");
      } else if (typeKey === "org:name") {
        setOrgSearchQueryState((prev) => ({
          ...prev,
          roleId: "",
          orgName: currentTrimmed,
        }));
        setOrgSearchKey((k) => k + 1);
        history.replace("/search/org");
      } else if (tab === "user") {
        setUserSearchQueryState((prev) => ({
          ...prev,
          userId: currentTrimmed,
        }));
        setUserSearchKey((k) => k + 1);
        history.replace("/search/user");
      } else if (tab === "org") {
        setOrgSearchQueryState((prev) => ({
          ...prev,
          roleId: currentTrimmed,
        }));
        setOrgSearchKey((k) => k + 1);
        history.replace("/search/org");
      } else {
        setDocSearchQueryState((prev) => ({
          ...prev,
          docId: currentTrimmed,
          docType: type?.docType ?? NO_DISPLAY_DATA,
        }));
        setDocSearchKey((k) => k + 1);
        history.replace("/search/doc");
      }

      handleClose();
    },
    [
      history,
      setSearchTab,
      setDocSearchKey,
      setUserSearchKey,
      setOrgSearchKey,
      setUserSearchQueryState,
      setOrgSearchQueryState,
      setDocSearchQueryState,
      handleClose,
    ]
  );

  const handleSearch = useCallback(
    (useSecondary = false) => {
      const currentSecondaryKeys = secondaryKeysRef.current;
      const currentEffectiveKey = effectiveKeyRef.current;
      const key =
        useSecondary && currentSecondaryKeys.length > 0
          ? currentSecondaryKeys[0]
          : currentEffectiveKey;
      navigate(key);
    },
    [navigate]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (openRef.current && e.key === "Enter") {
        e.preventDefault();
        handleSearch(e.altKey || e.metaKey);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSearch]);

  const handleChipClick = (key: string) => {
    const newKey = selectedKey === key ? null : key;
    setSelectedKey(newKey);
    if (trimmed) {
      navigate(newKey ?? autoDetectTypeKey(trimmed));
    }
  };

  const renderChip = (type: SearchTypeOption) => {
    const active = effectiveKey === type.key;
    const secondary = !active && secondaryKeys.includes(type.key);
    return (
      <Chip
        key={type.key}
        label={type.label}
        size="small"
        color={active ? "primary" : secondary ? "warning" : "default"}
        variant={active ? "filled" : "outlined"}
        onClick={() => handleChipClick(type.key)}
        sx={{ cursor: "pointer", ...(active && { fontWeight: 600 }) }}
      />
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        <TextField
          fullWidth
          inputRef={inputRef}
          placeholder="Search by ID, email, or name..."
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setSelectedKey(null);
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 80, pt: 0.25 }}
            >
              ID:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {ID_TYPES.map(renderChip)}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 80, pt: 0.25 }}
            >
              Email:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {EMAIL_TYPES.map(renderChip)}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 80, pt: 0.25 }}
            >
              Name:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {NAME_TYPES.map(renderChip)}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 80, pt: 0.25 }}
            >
              Document:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {DOC_TYPES.map(renderChip)}
            </Box>
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary">
          {secondaryKeys.length > 0
            ? `Enter to search · ⌘Enter for ${
                secondaryKeys[0] === "org:name"
                  ? "org name"
                  : secondaryKeys[0] === "org"
                  ? "org ID"
                  : "document"
              } search · Esc to close · ⌘K to open`
            : "Enter to search · Esc to close · ⌘K to open"}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearchDialog;
